import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Trash2, X } from 'lucide-react';
import {
  useHistory,
  useDeleteTranslation,
  useClearHistory,
  useToggleFavorite,
  downloadHistoryExport,
} from '@/api/hooks';
import { BackButton } from '@/components/BackButton';
import { HistoryItemCard } from '@/components/HistoryItemCard';
import { LoadingPlaceholder } from '@/components/LoadingPlaceholder';

export function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showExport, setShowExport] = useState(false);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useHistory(20);
  const del = useDeleteTranslation();
  const clear = useClearHistory();
  const fav = useToggleFavorite();

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  const handleClear = () => {
    if (window.confirm(t('history.clearConfirm'))) {
      clear.mutate();
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm(t('lists.deleteTranslationConfirm'))) {
      del.mutate(id);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/lists');
  };

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <BackButton onClick={handleBack} />
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => setShowExport((s) => !s)}
            className="btn-secondary h-8 px-2"
            aria-label={showExport ? t('history.exportClose') : t('history.export')}
            title={showExport ? t('history.exportClose') : t('history.export')}
          >
            {showExport ? <X className="h-4 w-4" /> : <Download className="h-4 w-4" />}
          </button>
          {items.length > 0 && (
            <button onClick={handleClear} className="btn-ghost h-8 px-2 text-rose-600">
              <Trash2 className="h-4 w-4" />
              {t('history.clearAll')}
            </button>
          )}
        </div>
      </div>

      <h1 className="text-xl font-semibold">{t('history.title')}</h1>

      {showExport && (
        <div className="card flex gap-2 p-3">
          <button
            className="btn-secondary flex-1"
            onClick={() => {
              void downloadHistoryExport('json');
              setShowExport(false);
            }}
          >
            {t('history.exportJson')}
          </button>
          <button
            className="btn-secondary flex-1"
            onClick={() => {
              void downloadHistoryExport('txt');
              setShowExport(false);
            }}
          >
            {t('history.exportTxt')}
          </button>
        </div>
      )}

      {isLoading ? (
        <LoadingPlaceholder />
      ) : items.length === 0 ? (
        <p className="text-slate-500">{t('history.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <HistoryItemCard
                item={item}
                onLoadIntoForm={() => navigate('/', { state: { useAsInput: item } })}
                onToggleFavorite={() =>
                  fav.mutate({ translationId: item.id, isFavorite: !item.isFavorite })
                }
                onOpenLists={() => navigate(`/items/${item.id}/lists`)}
                onDelete={() => handleDelete(item.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <button
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="btn-secondary mt-4 w-full"
        >
          {t('history.loadMore')}
        </button>
      )}
    </div>
  );
}
